import React, { useMemo, MutableRefObject, useEffect, useCallback, useState, RefCallback } from 'react';
import styled from 'styled-components/macro';
import { format, isToday, parseISO, startOfDay } from 'date-fns';
import MessageGroup from '../../ChannelMessage/MessageGroup';
import { IMap } from 'shared/interfaces/IMap';
import {
  ChannelMessageAttachmentType,
  ChannelViewChannelFragment,
  ChannelViewChannelMessageFragment,
  GetClinicChannelViewChannelQuery,
  SmsNotificationStatusFragment,
  useGetClinicAutomationsQuery,
} from 'shared/types/graphql';
import { useSidePanel } from 'shared/components/SidePanel/state/providers/SidePanelProvider';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import { INotificationErrorTextProps } from '..';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import useLastMessage from 'pages/Conversations/hooks/useLastMessage';
import { Box, Center, keyframes } from '@televet/kibble-ui/build/chakra';
import AiSummaryPopover from './AiSummaryPopover';
import { useSummaryProvider } from 'shared/providers/SummaryProvider';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

interface IMessageTimelineProps {
  messages: ChannelViewChannelMessageFragment[] | undefined;
  isChannelMessagesLoading: boolean;
  channelId: string | undefined;
  hasPreviousPage: boolean;
  channelData?: ChannelViewChannelFragment | null;
  isTeamChannel: boolean;
  messageTimelineScrollContainerRef: MutableRefObject<HTMLDivElement | null>;
  currentUserId?: string;
  previousMessagesRef: RefCallback<HTMLDivElement | null>;
  onLastMessageMount: (message: ChannelViewChannelMessageFragment) => void;
  setPreviewData: (fileUrl: string, attachmentType: ChannelMessageAttachmentType, fileName: string) => void;
  setCurrentInvoiceId: (invoiceId: string) => void;
  setInvoiceClinicPetParentId: (invoiceClinicPetParentId: string) => void;
  phoneWarnings: INotificationErrorTextProps[];
  emailWarnings: INotificationErrorTextProps[];
  lastConsumedMessageIndex: number | undefined | null;
  channelMemberId: string | undefined;
  newMessageIndicatorRef: MutableRefObject<HTMLDivElement | null>;
  clinicPetParents: NonNullable<
    GetClinicChannelViewChannelQuery['findUniqueChannel']
  >['channelMembers'][0]['clinicPetParent'][];
}

const MessageTimeline = ({
  messages,
  isChannelMessagesLoading,
  channelId,
  hasPreviousPage,
  channelData,
  isTeamChannel,
  messageTimelineScrollContainerRef,
  currentUserId,
  previousMessagesRef,
  onLastMessageMount,
  setPreviewData,
  setCurrentInvoiceId,
  setInvoiceClinicPetParentId,
  phoneWarnings,
  emailWarnings,
  lastConsumedMessageIndex,
  channelMemberId,
  newMessageIndicatorRef,
  clinicPetParents,
}: IMessageTimelineProps): JSX.Element | null => {
  const [smsStstusesByMessage, setSmsStstusesByMessage] = useState<Record<string, SmsNotificationStatusFragment>>({});
  const [hasScrolledUp, setHasScrolledUp] = useState(false);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const { isSummaryButtonHidden } = useSummaryProvider();

  const messagesByDay = useMemo(() => {
    if (!messages?.length || !channelId) return {};

    // Group messages by day
    const messageGroup = messages.reduce(
      (
        dayGroup: IMap<ChannelViewChannelMessageFragment[]>,
        currentMessage: ChannelViewChannelMessageFragment,
      ): IMap<ChannelViewChannelMessageFragment[]> => {
        const startTimeString = startOfDay(parseISO(currentMessage.createdAt)).toISOString();
        (dayGroup[startTimeString] = dayGroup[startTimeString] || []).unshift(currentMessage);
        return dayGroup;
      },
      {} as IMap<ChannelViewChannelMessageFragment[]>,
    );

    // Sort messages within each day from oldest to newest
    Object.keys(messageGroup).forEach((key) => {
      messageGroup[key].sort((a, b) => a.index - b.index);
    });

    const ordered = {} as IMap<ChannelViewChannelMessageFragment[]>;

    // Sort each day from newest to oldest
    Object.keys(messageGroup)
      .sort((a: string, b: string) => parseISO(b).getTime() - parseISO(a).getTime())
      .forEach((key) => {
        ordered[key] = messageGroup[key];
      });

    return ordered;
  }, [messages, channelId]);

  // In mobile once the side panel is open and hitting back
  // on the browser it returns to the conversation but we
  // need to close the side panel.

  const { setIsOpen, isOpen } = useSidePanel();
  const { isMobile } = useResolutionProvider();
  useEffect(() => {
    if (isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [isMobile, isOpen, setIsOpen]);

  const { currentClinicId } = useClinicUser();

  const { data: workflowsData } = useGetClinicAutomationsQuery({
    variables: { clinicId: currentClinicId ?? '' },
    skip: !currentClinicId,
  });

  const workflowEventSettingsById = useMemo(() => {
    const obj = {} as IMap<string>;
    if (workflowsData?.findManyWorkflowEventSetting) {
      for (const workflowEventSetting of workflowsData.findManyWorkflowEventSetting) {
        obj[workflowEventSetting.id] = workflowEventSetting.name;
      }
    }
    return obj;
  }, [workflowsData]);

  const { lastEligibleMessageIndex, lastEligibleMessageReadByMember } = useLastMessage({
    channelData,
    currentUserId,
    messages,
  });

  const handleSmsStatusChange = useCallback((status: SmsNotificationStatusFragment, messageId: string) => {
    setSmsStstusesByMessage((value) => ({
      ...value,
      [messageId]: status,
    }));
  }, []);

  useEffect(() => {
    const scrollContainer = messageTimelineScrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = (): void => {
      const { scrollTop } = scrollContainer;

      // the user needs to scroll up 100px before showing the AI summary
      if (scrollTop < lastScrollTop && scrollTop < scrollContainer.scrollHeight - scrollContainer.clientHeight - 100) {
        setHasScrolledUp(true);
      }

      setLastScrollTop(scrollTop);
    };

    scrollContainer.addEventListener('scroll', handleScroll);

    return (): void => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [messageTimelineScrollContainerRef, lastScrollTop]);

  // Reset hasScrolledUp when channel changes
  useEffect(() => {
    setHasScrolledUp(false);
    setLastScrollTop(0);
  }, [channelId]);

  const shouldDisplayAiSummary = hasScrolledUp || isSummaryButtonHidden;

  return (
    <MessageTimelineContainer data-testid="message-timeline">
      {shouldDisplayAiSummary && (
        <Box
          position="absolute"
          top={0}
          zIndex={10}
          display="flex"
          justifyContent="center"
          width="calc(100% - 15px)"
          pt={2}
          backgroundColor="transparent"
          animation={`${fadeInUp} 0.4s ease-out forwards`}
          pointerEvents="none"
        >
          <Box pointerEvents="auto">
            <AiSummaryPopover channelId={channelId} />
          </Box>
        </Box>
      )}

      <MessageTimelineScrollContainer ref={messageTimelineScrollContainerRef}>
        <>
          {Object.keys(messagesByDay).map((key, dayIndex) => {
            return (
              <MessageGroupContainer key={key}>
                <DaySeparatorContainer>
                  <Line />
                  <DayLabel>{isToday(parseISO(key)) ? 'Today' : format(parseISO(key), 'EEEE, MMMM do')}</DayLabel>
                  <Line />
                </DaySeparatorContainer>
                {messagesByDay[key].map((message: ChannelViewChannelMessageFragment, messageIndex, dayMessages) => {
                  const isLastMessage = dayIndex === 0 && messageIndex === dayMessages.length - 1;
                  const isFirstUnreadMessage =
                    lastConsumedMessageIndex != null ? lastConsumedMessageIndex + 1 === message.index : false;
                  if (!message.body && !message.attachments?.length && !message.deletedAt) return null;
                  return (
                    <React.Fragment key={message.id}>
                      {isFirstUnreadMessage && <NewMessagesIndicator ref={newMessageIndicatorRef} />}
                      <MessageGroup
                        key={message.id}
                        message={message}
                        channelData={channelData}
                        setPreviewData={setPreviewData}
                        onLastMessageMount={onLastMessageMount}
                        workflowEventSettingsById={workflowEventSettingsById}
                        setCurrentInvoiceId={setCurrentInvoiceId}
                        setInvoiceClinicPetParentId={setInvoiceClinicPetParentId}
                        phoneWarnings={phoneWarnings}
                        emailWarnings={emailWarnings}
                        channelMemberId={channelMemberId}
                        clinicPetParents={clinicPetParents}
                        seenByMembers={lastEligibleMessageReadByMember[message.index]}
                        isLastEligibleMessage={lastEligibleMessageIndex === message.index}
                        isTeamChannel={isTeamChannel}
                        currentUserId={currentUserId}
                        onSmsStatusChange={handleSmsStatusChange}
                        smsNotificationStatus={smsStstusesByMessage[message.id]}
                        isLastMessage={isLastMessage}
                      />
                    </React.Fragment>
                  );
                })}
              </MessageGroupContainer>
            );
          })}
          {(hasPreviousPage || isChannelMessagesLoading) && (
            <Center my={6} sx={{ overflowAnchor: 'none' }} w="100%" ref={previousMessagesRef}>
              <Spinner />
            </Center>
          )}
          {
            // add size similar to the one occupied by the button above
            shouldDisplayAiSummary && <Box mt="35px" sx={{ overflowAnchor: 'none' }} />
          }
        </>
      </MessageTimelineScrollContainer>
    </MessageTimelineContainer>
  );
};

const MessageTimelineContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  overflow-x: hidden;
`;

const MessageTimelineScrollContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-start;
  justify-content: flex-start;
  overflow-anchor: none;
`;

const MessageGroupContainer = styled.div`
  width: 100%;
  overflow-anchor: none;
`;

const DaySeparatorContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 10px 0 10px 0;
  text-align: center;
  width: 100%;
`;

const Line = styled.div`
  border-top: 1px solid #ebedf0;
  flex: 1;
  height: 1px;
`;

const DayLabel = styled.span`
  background-color: var(--chakra-colors-background-default);
  border-radius: 14.5px;
  border: none;
  border: solid 1px #ebedf0;
  color: var(--chakra-colors-text-subtle);
  font-size: 12px;
  height: 25px;
  padding: 4px 1em 0 1em;
`;

const NewMessagesIndicator = styled.div`
  width: 100%;
  position: relative;
  margin-right: 100px;
  height: 0;
  border-bottom: 1px solid red;
  color: red;
  scroll-margin-top: 40px;
  z-index: 1;
  &::after {
    content: 'NEW';
    font-size: 12px;
    font-weight: 600;
    position: absolute;
    right: 10px;
    background-color: white;
    top: -10px;
    padding: 0 6px;
    border-radius: 6px;
    border: 1px solid red;
    text-align: center;
  }
`;

export default MessageTimeline;
